import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import { IconPicker } from '../IconPicker';
import { offlinePlayersApi } from '../../api';
import type { OfflinePlayer } from '../../types';

interface PlayerRow {
  id: string;
  displayName: string;
  handle: string;
  iconName: string;
  status: 'pending' | 'saving' | 'success' | 'error';
  error?: string;
}

export interface BulkImportPlayersDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete: (players: OfflinePlayer[]) => void;
}

const createEmptyRow = (): PlayerRow => ({
  id: Math.random().toString(36).substr(2, 9),
  displayName: '',
  handle: '',
  iconName: '',
  status: 'pending',
});

export const BulkImportPlayersDialog: React.FC<BulkImportPlayersDialogProps> = ({
  open,
  onClose,
  onComplete,
}) => {
  const [rows, setRows] = useState<PlayerRow[]>(() =>
    Array.from({ length: 5 }, createEmptyRow)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdPlayers, setCreatedPlayers] = useState<OfflinePlayer[]>([]);

  const handleClose = () => {
    if (createdPlayers.length > 0) {
      onComplete(createdPlayers);
    }
    // Reset state
    setRows(Array.from({ length: 5 }, createEmptyRow));
    setError(null);
    setCreatedPlayers([]);
    onClose();
  };

  const updateRow = (id: string, field: 'displayName' | 'handle' | 'iconName', value: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === id
          ? { ...row, [field]: field === 'handle' ? value.toUpperCase().slice(0, 4) : value }
          : row
      )
    );
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const addMoreRows = () => {
    setRows((prev) => [...prev, ...Array.from({ length: 5 }, createEmptyRow)]);
  };

  const handleImport = async () => {
    // Filter rows with display names
    const validRows = rows.filter((row) => row.displayName.trim() && row.status !== 'success');

    if (validRows.length === 0) {
      setError('Please enter at least one player name');
      return;
    }

    setSaving(true);
    setError(null);

    const newCreatedPlayers: OfflinePlayer[] = [];

    // Process each row
    for (const row of validRows) {
      // Update status to saving
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, status: 'saving' as const } : r))
      );

      try {
        const player = await offlinePlayersApi.createMyOfflinePlayer({
          displayName: row.displayName.trim(),
          handle: row.handle.trim() || undefined,
          iconName: row.iconName.trim() || undefined,
        });
        newCreatedPlayers.push(player);

        // Update status to success
        setRows((prev) =>
          prev.map((r) => (r.id === row.id ? { ...r, status: 'success' as const } : r))
        );
      } catch (err) {
        // Update status to error
        setRows((prev) =>
          prev.map((r) =>
            r.id === row.id
              ? {
                  ...r,
                  status: 'error' as const,
                  error: err instanceof Error ? err.message : 'Failed',
                }
              : r
          )
        );
      }
    }

    setCreatedPlayers((prev) => [...prev, ...newCreatedPlayers]);
    setSaving(false);
  };

  const pendingCount = rows.filter((r) => r.displayName.trim() && r.status === 'pending').length;
  const successCount = rows.filter((r) => r.status === 'success').length;
  const totalCreated = createdPlayers.length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          maxHeight: { xs: '90vh', sm: '80vh' },
          m: { xs: 1, sm: 2 },
        },
      }}
    >
      <DialogTitle>
        Bulk Import Offline Players
        {totalCreated > 0 && (
          <Typography variant="body2" color="success.main">
            {totalCreated} player{totalCreated !== 1 ? 's' : ''} created
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ overflow: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add multiple players at once. <strong>Display Name</strong> is required.
          <strong> Handle</strong> (1-4 chars shown on squares) and <strong>Icon</strong> (emoji shown on squares) are optional.
        </Typography>

        {/* Column headers - hide on mobile */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1, mb: 1, px: 1, alignItems: 'center' }}>
          <Box sx={{ width: 24 }} />
          <Typography variant="caption" sx={{ flex: 2, fontWeight: 'bold' }}>
            Name *
          </Typography>
          <Typography variant="caption" sx={{ width: 80, fontWeight: 'bold' }}>
            Handle
          </Typography>
          <Typography variant="caption" sx={{ width: 120, fontWeight: 'bold' }}>
            Icon
          </Typography>
          <Box sx={{ width: 80 }} />
        </Box>

        <Divider sx={{ mb: 1, display: { xs: 'none', sm: 'block' } }} />

        {/* Player rows */}
        <Box sx={{ maxHeight: { xs: 'none', sm: 400 }, overflowY: { xs: 'visible', sm: 'auto' } }}>
          {rows.map((row, index) => (
            <Box
              key={row.id}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                alignItems: { xs: 'stretch', sm: 'center' },
                mb: { xs: 2, sm: 1 },
                opacity: row.status === 'success' ? 0.6 : 1,
                bgcolor: row.status === 'error' ? 'error.50' : { xs: 'grey.50', sm: 'transparent' },
                p: { xs: 1.5, sm: 0.5 },
                borderRadius: 1,
                border: { xs: 1, sm: 0 },
                borderColor: 'divider',
              }}
            >
              {/* Row number - visible on both */}
              <Typography
                variant="caption"
                sx={{ width: { xs: 'auto', sm: 24 }, textAlign: { xs: 'left', sm: 'right' }, color: 'text.secondary', fontWeight: { xs: 'bold', sm: 'normal' } }}
              >
                {index + 1}. {/* On mobile, show "Player N" */}
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Player</Box>
              </Typography>
              <TextField
                size="small"
                label={index === 0 ? 'Name *' : undefined}
                placeholder="John Smith"
                value={row.displayName}
                onChange={(e) => updateRow(row.id, 'displayName', e.target.value)}
                disabled={saving || row.status === 'success'}
                sx={{ flex: { xs: 1, sm: 2 } }}
                error={row.status === 'error'}
                helperText={row.status === 'error' ? row.error : undefined}
                fullWidth
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  label={index === 0 ? 'Handle' : undefined}
                  placeholder="JOHN"
                  value={row.handle}
                  onChange={(e) => updateRow(row.id, 'handle', e.target.value)}
                  disabled={saving || row.status === 'success'}
                  sx={{ width: { xs: 'auto', sm: 80 }, flex: { xs: 1, sm: 'none' } }}
                  inputProps={{ maxLength: 4 }}
                />
                <Box sx={{ width: { xs: 'auto', sm: 120 }, flex: { xs: 1, sm: 'none' } }}>
                  <IconPicker
                    value={row.iconName}
                    onChange={(value) => updateRow(row.id, 'iconName', value)}
                    disabled={saving || row.status === 'success'}
                    helperText=""
                    size="small"
                  />
                </Box>
              </Box>
              <Box sx={{ width: { xs: 'auto', sm: 80 }, display: 'flex', justifyContent: { xs: 'flex-end', sm: 'center' } }}>
                {row.status === 'success' ? (
                  <SuccessIcon color="success" />
                ) : row.status === 'saving' ? (
                  <Typography variant="caption" color="text.secondary">
                    Saving...
                  </Typography>
                ) : (
                  <IconButton
                    size="small"
                    onClick={() => removeRow(row.id)}
                    disabled={saving || rows.length <= 1}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          ))}
        </Box>

        <Button
          startIcon={<AddIcon />}
          onClick={addMoreRows}
          disabled={saving}
          size="small"
          sx={{ mt: 1 }}
        >
          Add 5 More Rows
        </Button>
      </DialogContent>
      <DialogActions>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, pl: 2 }}>
          {pendingCount > 0 && `${pendingCount} to import`}
          {pendingCount > 0 && successCount > 0 && ' | '}
          {successCount > 0 && `${successCount} created`}
        </Typography>
        <Button onClick={handleClose} disabled={saving}>
          {totalCreated > 0 ? 'Done' : 'Cancel'}
        </Button>
        {pendingCount > 0 && (
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={saving || pendingCount === 0}
          >
            {saving ? 'Importing...' : `Import ${pendingCount} Player${pendingCount !== 1 ? 's' : ''}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
